import { Component, EventEmitter, HostListener, OnDestroy, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageService } from 'primeng/api';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  CadastreParcel,
  CadastreParcelMapDto,
  CadastreParcelService,
} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { OldMapComponent } from '@components/old-map/old-map.component';
import { environment } from '@root/src/environments/environment';
import { CADASTRE_PARCEL_CRUD_ENABLED } from '@app/pages/electronic-field-map/pages/gis-fields/managers/cadastre-parcel-crud.constants';

type CadastreParcelMapView = CadastreParcelMapDto & {
  districtSoato?: string | null;
};

@Component({
  selector: 'app-cadastre-parcel-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    OldMapComponent,
  ],
  templateUrl: './cadastre-parcel-details-dialog.component.html',
  styleUrls: ['./cadastre-parcel-details-dialog.component.scss'],
})
export class CadastreParcelDetailsDialogComponent implements OnDestroy {
  private readonly cadastreParcelService = inject(CadastreParcelService);
  private readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly destroy$ = new Subject<void>();

  /** When false, create/edit/delete UI stays hidden while CRUD code remains ready. */
  readonly crudEnabled = CADASTRE_PARCEL_CRUD_ENABLED;

  @Output() save = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  visible = false;
  editing = false;
  readonlyMode = false;
  creating = false;
  saving = false;
  validationError: string | null = null;
  mapCenter: [number, number] = [environment.map.lng, environment.map.lat];
  parcel: CadastreParcel | null = null;
  draft: CadastreParcel = this.emptyDraft();

  show(mapParcel: CadastreParcelMapView): void {
    this.creating = false;
    this.editing = false;
    this.readonlyMode = false;
    this.visible = true;
    this.validationError = null;
    if (mapParcel.centerX != null && mapParcel.centerY != null) {
      this.mapCenter = [mapParcel.centerX, mapParcel.centerY];
    }
    this.parcel = {
      id: mapParcel.id,
      cadNumber: mapParcel.cadNumber,
      area: mapParcel.area,
      regionId: mapParcel.regionId,
      districtSoato: mapParcel.districtSoato,
      landType: mapParcel.landType,
      address: mapParcel.address,
      taxNumber: mapParcel.taxNumber,
      geometryJson: mapParcel.geometryJson,
    };
    this.draft = { ...this.parcel };

    if (mapParcel.id) {
      this.cadastreParcelService
        .cropMonitoringCadastreParcelGetByIdIdGet(mapParcel.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (full) => {
            this.parcel = full;
            this.draft = { ...full };
          },
          error: () => {
            /* keep map projection */
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * The dialog is rendered outside the component host (appendTo="body"), so the browser copy
   * shortcut is handled here for selections made inside the parcel details block.
   */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent): void {
    if (!this.visible || !(event.ctrlKey || event.metaKey) || event.key?.toLowerCase() !== 'c') {
      return;
    }

    const selection = window.getSelection();
    const selectedText = selection?.toString() ?? '';
    if (!selectedText || !this.isSelectionInsideDetails(selection)) {
      return;
    }

    void navigator.clipboard?.writeText(selectedText).catch(() => undefined);
  }

  private isSelectionInsideDetails(selection: Selection | null): boolean {
    const node = selection?.anchorNode ?? null;
    const element = node instanceof Element ? node : node?.parentElement ?? null;
    return !!element?.closest('.cadastre-details');
  }

  showCreate(seed?: Partial<CadastreParcel>): void {
    if (!this.crudEnabled) {
      return;
    }

    this.creating = true;
    this.editing = true;
    this.readonlyMode = false;
    this.visible = true;
    this.validationError = null;
    this.parcel = null;
    this.draft = {
      ...this.emptyDraft(),
      ...seed,
    };
    if (seed?.coordinateLng != null && seed?.coordinateLat != null) {
      this.mapCenter = [seed.coordinateLng, seed.coordinateLat];
    }
  }

  onHide(): void {
    this.visible = false;
    this.editing = false;
    this.readonlyMode = false;
    this.creating = false;
    this.close.emit();
  }

  startEdit(): void {
    if (!this.crudEnabled || !this.parcel) {
      return;
    }
    this.draft = { ...this.parcel };
    this.validationError = null;
    this.readonlyMode = false;
    this.editing = true;
  }

  startReadonlyView(): void {
    if (!this.parcel) {
      return;
    }

    this.draft = { ...this.parcel };
    this.validationError = null;
    this.readonlyMode = true;
    this.editing = true;
  }

  cancelEdit(): void {
    if (this.creating) {
      this.onHide();
      return;
    }
    this.editing = false;
    this.readonlyMode = false;
    if (this.parcel) {
      this.draft = { ...this.parcel };
    }
    this.validationError = null;
  }

  onGeometryChange(geometry: unknown): void {
    const wkt =
      typeof geometry === 'string'
        ? geometry
        : (geometry as any)?.polygonJson ?? (geometry as any)?.geometry;

    if (!wkt || typeof wkt !== 'string') {
      return;
    }

    this.draft.geometryJson = wkt;
    const coordinates = this.parsePolygonCoordinates(wkt);
    if (!coordinates.length) {
      return;
    }

    const lng = coordinates.reduce((sum, point) => sum + point.lng, 0) / coordinates.length;
    const lat = coordinates.reduce((sum, point) => sum + point.lat, 0) / coordinates.length;
    this.draft.coordinateLng = Number(lng.toFixed(10));
    this.draft.coordinateLat = Number(lat.toFixed(10));
    this.mapCenter = [this.draft.coordinateLng, this.draft.coordinateLat];
  }

  onValidationError(error: string | null): void {
    this.validationError = error
      ? this.translate.instant(error)
      : null;
  }

  onAreaChange(area: unknown): void {
    const parsed = Number(area);
    this.draft.area = Number.isFinite(parsed) ? parsed : null;
  }

  saveParcel(): void {
    if (!this.crudEnabled) {
      return;
    }
    if (this.validationError) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('Cadastre'),
        detail: this.validationError,
      });
      return;
    }
    if (!this.draft.cadNumber?.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('Cadastre'),
        detail: this.translate.instant('Cadastral number is required'),
      });
      return;
    }
    if (!this.draft.geometryJson?.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('Cadastre'),
        detail: this.translate.instant('Geometry is required'),
      });
      return;
    }

    this.saving = true;
    const payload: CadastreParcel = {
      ...this.draft,
      cadNumber: this.draft.cadNumber.trim(),
      geometryJson: this.draft.geometryJson.trim(),
    };

    const request$: Observable<string | CadastreParcel> = this.creating
      ? this.cadastreParcelService.cropMonitoringCadastreParcelCreatePost(payload)
      : this.cadastreParcelService.cropMonitoringCadastreParcelUpdatePut(payload);

    request$.subscribe({
      next: () => {
        this.saving = false;
        this.editing = false;
        this.creating = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('Cadastre'),
          detail: this.translate.instant('Saved'),
        });
        this.visible = false;
        this.save.emit();
      },
      error: (err: any) => {
        this.saving = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('Cadastre'),
          detail: err?.error?.message || err?.message || this.translate.instant('Error'),
        });
      },
    });
  }

  deleteParcel(): void {
    if (!this.crudEnabled || !this.parcel?.id || this.creating) {
      return;
    }
    if (!confirm(this.translate.instant('Delete cadastre parcel?'))) {
      return;
    }

    this.saving = true;
    this.cadastreParcelService
      .cropMonitoringCadastreParcelDeleteIdDelete(this.parcel.id)
      .subscribe({
        next: () => {
          this.saving = false;
          this.visible = false;
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('Cadastre'),
            detail: this.translate.instant('Deleted'),
          });
          this.save.emit();
        },
        error: (err) => {
          this.saving = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('Cadastre'),
            detail: err?.error?.message || err?.message || this.translate.instant('Error'),
          });
        },
      });
  }

  private emptyDraft(): CadastreParcel {
    return {
      cadNumber: '',
      taxNumber: null,
      districtSoato: null,
      regionId: null,
      area: null,
      address: null,
      landType: null,
      geometryJson: '',
    };
  }

  private parsePolygonCoordinates(wkt: string): Array<{ lng: number; lat: number }> {
    const match = wkt.match(/POLYGON\s*\(\(([^)]+)\)\)/i);
    if (!match) {
      return [];
    }

    const coordinates = match[1]
      .split(',')
      .map((pair) => pair.trim().split(/\s+/).map(Number))
      .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat))
      .map(([lng, lat]) => ({ lng, lat }));

    if (coordinates.length > 1) {
      const first = coordinates[0];
      const last = coordinates[coordinates.length - 1];
      if (first.lng === last.lng && first.lat === last.lat) {
        coordinates.pop();
      }
    }

    return coordinates;
  }
}
